import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { FileText, ExternalLink, CheckCircle2, AlertCircle } from "lucide-react";
import { api } from "../../lib/auth";

export default function AdminPendingPayments() {
  const [rows, setRows] = useState([]);

  const load = () => api.get("/admin/pending-payments").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const action = async (tid, status, reason = "") => {
    try {
      await api.post(`/admin/tenants/${tid}/status`, { status, reason });
      toast.success(`Tenant ${status}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const total = rows.length;
  const totalAmount = rows.reduce((sum, r) => sum + (r.amount || 0), 0);

  return (
    <div className="p-8 lg:p-10 max-w-[1400px] space-y-6">

      {/* 🔥 Header */}
      <div>
        <h1 className="font-display font-extrabold text-4xl text-black tracking-tight">
          Pending Payments
        </h1>
        <p className="text-black/60 mt-1 text-sm">
          Payments awaiting manual verification.
        </p>
      </div>

      {/* 📊 Stats */}
      <div className="grid md:grid-cols-3 gap-4">
        <div className="bg-[#EEF6FF] rounded-2xl p-5 border border-black/5">
          <div className="text-xs text-black/50">Pending Requests</div>
          <div className="text-2xl font-bold text-black mt-1">{total}</div>
        </div>

        <div className="bg-[#FFF5D6] rounded-2xl p-5 border border-black/5">
          <div className="text-xs text-black/50">Total Value</div>
          <div className="text-2xl font-bold text-black mt-1">
            ₦{totalAmount.toLocaleString()}
          </div>
        </div>

        <div className="bg-[#F4F8F3] rounded-2xl p-5 border border-black/5">
          <div className="text-xs text-black/50">Action Needed</div>
          <div className="text-2xl font-bold text-[#1FA868] mt-1">
            {total > 0 ? "Yes" : "None"}
          </div>
        </div>
      </div>

      {/* 📋 List */}
      <div className="space-y-4">
        {rows.map((t) => (
          <div
            key={t.id}
            className="bg-white border border-black/8 rounded-2xl p-5 flex flex-wrap items-center gap-4 justify-between hover:shadow-sm transition"
          >

            {/* LEFT */}
            <div className="flex items-center gap-4 min-w-0">
              <div className="w-12 h-12 rounded-xl bg-[#F4F8F3] text-[#1B4332] flex items-center justify-center font-bold text-sm">
                {(t.company_name || "?").slice(0, 2).toUpperCase()}
              </div>

              <div className="min-w-0">
                <Link
                  to={`/admin/tenants/${t.id}`}
                  className="font-semibold text-black hover:text-[#1FA868]"
                >
                  {t.company_name}
                </Link>

                <div className="text-xs text-black/55 truncate">
                  {t.admin_email}
                </div>

                <div className="mt-1 text-xs text-black/50 flex flex-wrap gap-2">
                  <span className="bg-[#EEF6FF] px-2 py-0.5 rounded-md">
                    {t.plan_name}
                  </span>
                  <span className="bg-[#FFF5D6] px-2 py-0.5 rounded-md font-semibold">
                    ₦{Number(t.amount || 0).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>

            {/* RIGHT */}
            <div className="flex items-center gap-2 flex-wrap">

              {t.payment_proof_url && (
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}${t.payment_proof_url}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white border border-black/10 text-xs font-semibold hover:border-black/25"
                >
                  <FileText size={12} />
                  Proof
                  <ExternalLink size={11} />
                </a>
              )}

              <button
                onClick={() => action(t.id, "active")}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#1FA868] text-white text-xs font-semibold hover:bg-[#16834D]"
              >
                <CheckCircle2 size={12} />
                Approve
              </button>

              <button
                onClick={() => {
                  const r = window.prompt("Rejection reason?");
                  if (r !== null) action(t.id, "rejected", r);
                }}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-rose-50 text-rose-600 text-xs font-semibold hover:bg-rose-100"
              >
                <AlertCircle size={12} />
                Reject
              </button>
            </div>
          </div>
        ))}

        {rows.length === 0 && (
          <div className="bg-white border border-black/8 rounded-2xl p-12 text-center text-black/50">
            No pending payments right now.
          </div>
        )}
      </div>
    </div>
  );
}